import {
  Alert,
  Box,
  Card,
  Grid,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import type { Practice } from '../../users/types/rbac';

export interface PracticeProfileContentProps {
  practice: Practice;
  canEdit: boolean;
  /** Called when profile fields change so parent can sync state for the top Save. */
  onProfileChange?: (updates: Partial<Practice>) => void;
}

export function PracticeProfileContent({
  practice,
  canEdit,
  onProfileChange,
}: PracticeProfileContentProps): JSX.Element {
  const le = practice.legalEntityAddress;
  const [name, setName] = useState(practice.name);
  const [legalName, setLegalName] = useState(practice.legalName ?? practice.name);
  const [email, setEmail] = useState(practice.email ?? '');
  const [phone, setPhone] = useState(practice.phone ?? '');
  const [websiteUrl, setWebsiteUrl] = useState(practice.websiteUrl ?? '');
  const [cancellationWindowHours, setCancellationWindowHours] = useState<number | ''>(
    practice.cancellationWindowHours ?? '',
  );
  const [missedSessionFee, setMissedSessionFee] = useState(practice.missedSessionFee ?? '');
  const [emailResponseTimeframe, setEmailResponseTimeframe] = useState(
    practice.emailResponseTimeframe ?? '',
  );
  const [taxId, setTaxId] = useState(practice.taxId ?? '');
  const [npi, setNpi] = useState(practice.npi ?? '');
  const [status, setStatus] = useState(practice.status);
  const [legalAddress, setLegalAddress] = useState({
    addressLine1: le?.addressLine1 ?? (le as { address?: string })?.address ?? '',
    addressLine2: le?.addressLine2 ?? '',
    city: le?.city ?? '',
    state: le?.state ?? '',
    zipCode: le?.zipCode ?? '',
  });
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setName(practice.name);
    setLegalName(practice.legalName ?? practice.name);
    setEmail(practice.email ?? '');
    setPhone(practice.phone ?? '');
    setWebsiteUrl(practice.websiteUrl ?? '');
    setCancellationWindowHours(practice.cancellationWindowHours ?? '');
    setMissedSessionFee(practice.missedSessionFee ?? '');
    setEmailResponseTimeframe(practice.emailResponseTimeframe ?? '');
    setTaxId(practice.taxId ?? '');
    setNpi(practice.npi ?? '');
    setStatus(practice.status);
    const lea = practice.legalEntityAddress;
    const legacy = lea as { address?: string } | undefined;
    setLegalAddress({
      addressLine1: lea?.addressLine1 ?? legacy?.address ?? '',
      addressLine2: lea?.addressLine2 ?? '',
      city: lea?.city ?? '',
      state: lea?.state ?? '',
      zipCode: lea?.zipCode ?? '',
    });
  }, [
    practice.id,
    practice.name,
    practice.legalName,
    practice.email,
    practice.phone,
    practice.websiteUrl,
    practice.cancellationWindowHours,
    practice.missedSessionFee,
    practice.emailResponseTimeframe,
    practice.taxId,
    practice.npi,
    practice.status,
    practice.legalEntityAddress,
  ]);

  const brandingDirty =
    name !== practice.name || legalName !== (practice.legalName ?? practice.name);
  const lastUpdated = practice.profileAudit?.lastUpdatedAt;
  const lastUpdatedBy = practice.profileAudit?.lastUpdatedBy;

  const updateLegalEntityAddress = (updates: Partial<typeof legalAddress>) => {
    const next = { ...legalAddress, ...updates };
    setLegalAddress(next);
    onProfileChange?.({
      legalEntityAddress: {
        addressLine1: next.addressLine1 || undefined,
        addressLine2: next.addressLine2 || undefined,
        city: next.city || undefined,
        state: next.state || undefined,
        zipCode: next.zipCode || undefined,
      },
    });
  };

  return (
    <Stack gap="lg">
      {/* Header: last updated */}
      {lastUpdated && (
        <Text size="xs" c="gray.6">
          Last updated
          {lastUpdatedBy ? ` by ${lastUpdatedBy}` : ''}{' '}
          {new Date(lastUpdated).toLocaleDateString('en-US')}
        </Text>
      )}

      {/* Practice information */}
      <Card withBorder>
        <Title order={4} mb="md">
          Practice information
        </Title>
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Practice owner"
              value={practice.ownerName}
              disabled
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Select
              label="Status"
              data={[
                { value: 'Active', label: 'Active' },
                { value: 'Inactive', label: 'Inactive' },
              ]}
              value={status}
              onChange={(v) => {
                if (v) {
                  setStatus(v as 'Active' | 'Inactive');
                  onProfileChange?.({ status: v as 'Active' | 'Inactive' });
                }
              }}
              disabled={!canEdit}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Total users"
              value={practice.userCount.toString()}
              disabled
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput label="Created date" value={practice.createdAt} disabled />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Website URL"
              placeholder="https://www.example.com"
              value={websiteUrl}
              onChange={(e) => {
                setWebsiteUrl(e.target.value);
                onProfileChange?.({ websiteUrl: e.target.value || undefined });
              }}
              disabled={!canEdit}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <NumberInput
              label="Cancellation window (hours)"
              placeholder="24"
              min={0}
              value={cancellationWindowHours}
              onChange={(value) => {
                const next = value === '' ? undefined : Number(value);
                setCancellationWindowHours(value === '' ? '' : Number(value));
                onProfileChange?.({ cancellationWindowHours: next });
              }}
              disabled={!canEdit}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Missed session fee"
              description="Displayed to clients for late cancellations and no-shows"
              placeholder="$150"
              value={missedSessionFee}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setMissedSessionFee(value);
                onProfileChange?.({ missedSessionFee: value });
              }}
              disabled={!canEdit}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Email response timeframe"
              placeholder="1–2 business days"
              value={emailResponseTimeframe}
              onChange={(e) => {
                setEmailResponseTimeframe(e.target.value);
                onProfileChange?.({ emailResponseTimeframe: e.target.value || undefined });
              }}
              disabled={!canEdit}
            />
          </Grid.Col>
        </Grid>
      </Card>

      {/* Legal Entity Information */}
      <Card withBorder>
        <Box mb="md">
          <Title order={4}>Legal Entity Information</Title>
          <Text size="sm" c="gray.6" mt="xs">
            Display and legal entity details for billing and client-facing surfaces.
          </Text>
        </Box>

        <Stack gap="md">
          <Grid>
            {/* Row 1: Display name | Legal entity name */}
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput
                label="Display name"
                description="Shown in portal, emails, and app"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  onProfileChange?.({ name: e.target.value });
                }}
                disabled={!canEdit}
                required
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput
                label="Legal entity name"
                description="Required for documents and billing"
                value={legalName}
                onChange={(e) => {
                  setLegalName(e.target.value);
                  onProfileChange?.({ legalName: e.target.value });
                }}
                disabled={!canEdit}
                required
                error={touched.legalName && !legalName.trim() ? 'Required' : undefined}
                onBlur={() => setTouched((t) => ({ ...t, legalName: true }))}
              />
            </Grid.Col>
            {/* Row 2: Email | Phone (practice) */}
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput
                label="Email"
                placeholder="contact@practice.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  onProfileChange?.({ email: e.target.value || undefined });
                }}
                disabled={!canEdit}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput
                label="Phone"
                placeholder="(555) 000-0000"
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value);
                  onProfileChange?.({ phone: e.target.value || undefined });
                }}
                disabled={!canEdit}
              />
            </Grid.Col>
            {/* Row 3: Address line 1 */}
            <Grid.Col span={12}>
              <TextInput
                label="Legal Entity Address 1"
                description="Street address for billing and documents"
                placeholder="123 Main St"
                value={legalAddress.addressLine1}
                onChange={(e) => updateLegalEntityAddress({ addressLine1: e.target.value })}
                disabled={!canEdit}
              />
            </Grid.Col>
            {/* Row 4: Address line 2 */}
            <Grid.Col span={12}>
              <TextInput
                label="Legal Entity Address 2"
                description="Suite, floor, building (optional)"
                placeholder="Suite 100"
                value={legalAddress.addressLine2}
                onChange={(e) => updateLegalEntityAddress({ addressLine2: e.target.value })}
                disabled={!canEdit}
              />
            </Grid.Col>
            {/* Row 5: City | State | ZIP (full row) */}
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput
                label="City"
                placeholder="City"
                value={legalAddress.city}
                onChange={(e) => updateLegalEntityAddress({ city: e.target.value })}
                disabled={!canEdit}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 3 }}>
              <TextInput
                label="State"
                placeholder="State"
                value={legalAddress.state}
                onChange={(e) => updateLegalEntityAddress({ state: e.target.value })}
                disabled={!canEdit}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 3 }}>
              <TextInput
                label="ZIP"
                placeholder="ZIP"
                value={legalAddress.zipCode}
                onChange={(e) => updateLegalEntityAddress({ zipCode: e.target.value })}
                disabled={!canEdit}
              />
            </Grid.Col>
            {/* Row 6: Tax ID */}
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput
                label="Tax ID (EIN)"
                description="Required for billing and compliance"
                value={taxId}
                onChange={(e) => {
                  setTaxId(e.target.value);
                  onProfileChange?.({ taxId: e.target.value });
                }}
                disabled={!canEdit}
                required
                placeholder="XX-XXXXXXX"
                error={touched.taxId && !taxId.trim() ? 'Required' : undefined}
                onBlur={() => setTouched((t) => ({ ...t, taxId: true }))}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput
                label="Practice NPI"
                description="National Provider Identifier for the practice"
                value={npi}
                onChange={(e) => {
                  setNpi(e.target.value);
                  onProfileChange?.({ npi: e.target.value || undefined });
                }}
                disabled={!canEdit}
                placeholder="1234567890"
              />
            </Grid.Col>
          </Grid>

          {brandingDirty && (
            <Alert color="blue" title="Client-facing impact">
              Changes will affect the client portal, emails, and telehealth waiting room.
            </Alert>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
